import { motion } from "framer-motion";
import { BreathworkWaitlistForm } from "@/components/BreathworkWaitlistForm";
import { boldify } from "@/lib/richtext";

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    initial={{ opacity: 0, y: 18 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: "easeOut", delay }}
    className={className}
  >
    {children}
  </motion.div>
);

const INCLUSIONS = [
  "Private one-on-one session",
  "Gentle, evidence-informed breathing techniques",
  "Quiet reflection time",
  "Studio A Reflection Journal prompts",
  "A personalised take-home breathing practice",
];

const OFFERINGS = [
  {
    slug: "breath-reset-30",
    name: "30 Minute Breath Reset",
    duration: "30 minutes",
    description:
      "A short, grounding session for the middle of an ordinary week. We slow the breath together, let the body settle, and you leave with **one simple practice** you can return to anywhere.",
  },
  {
    slug: "nervous-system-reset-60",
    name: "60 Minute Nervous System Reset",
    duration: "60 minutes",
    description:
      "A fuller hour of **guided breathing and stillness**. Time to arrive properly, work with the breath at an unhurried pace, and sit quietly with what surfaces — followed by **reflection prompts** and a **practice shaped to your daily life**.",
  },
  {
    slug: "breath-and-stillness-ritual-90",
    name: "90 Minute Breath & Stillness Ritual",
    duration: "90 minutes",
    description:
      "The most spacious of the three. **Guided breathing, extended stillness, and generous reflection time** — for when the season has been long and the body needs more than a pause. Nothing is rushed; nothing is asked of you.",
  },
];

export default function Breathwork() {
  return (
    <div data-testid="breathwork-page" className="pt-28 lg:pt-44 pb-32">
      <section className="max-w-[1100px] mx-auto px-6 sm:px-10">
        <Reveal>
          <div className="flex items-baseline gap-5 flex-wrap">
            <span className="label-eyebrow">One-on-One Breathwork</span>
            <span className="label-eyebrow text-[#A96A53]">Opening September</span>
          </div>
          <h1 className="serif mt-7 text-[48px] sm:text-[80px] leading-[1] tracking-tight">
            Learning to <span className="italic">breathe</span> slowly again.
          </h1>
          <p className="mt-12 max-w-2xl text-[18px] leading-[1.8] text-[#2E2B27]">
            Breathwork uses <strong>intentional breathing patterns</strong> to influence the body's stress response. Gentle, slow breathing has been shown to support <strong>parasympathetic activity</strong> — the part of the nervous system associated with rest, recovery and regulation. Every session is <strong>private, tailored to you</strong>, and free of performance or pressure. Simply space to breathe.
          </p>
        </Reveal>
      </section>

      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 mt-16">
        <img
          src="/images/editorial/ocean.jpg"
          alt="Calm, muted ocean at dawn — the rhythm breathwork returns you to."
          data-testid="breathwork-ocean-image"
          className="w-full aspect-[21/9] object-cover"
        />
      </section>

      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 mt-24">
        <div className="grid grid-cols-1 gap-8">
          {OFFERINGS.map((o, i) => (
            <Reveal key={o.slug} delay={i * 0.08}>
              <article data-testid={`breathwork-${o.slug}`} className="card-edge p-10 sm:p-14 bg-[#F7F6F2]">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                  <div className="lg:col-span-7">
                    <div className="label-eyebrow">{o.duration}</div>
                    <h2 className="serif mt-5 text-[30px] sm:text-[40px] leading-tight">{o.name}</h2>
                    <p className="mt-7 text-[16px] leading-[1.85] text-[#2E2B27] max-w-xl">{boldify(o.description)}</p>
                  </div>
                  <div className="lg:col-span-5 lg:border-l lg:border-[#D5CDBF] lg:pl-10">
                    <div className="label-eyebrow">Each session includes</div>
                    <ul className="mt-6 space-y-3">
                      {INCLUSIONS.map((inc) => (
                        <li key={inc} className="text-[15px] leading-relaxed text-[#55524C] border-t border-[#E7E2D6] pt-3 first:border-t-0 first:pt-0">
                          {inc}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      {/* EDITORIAL BREATH */}
      <section className="max-w-[1100px] mx-auto px-6 sm:px-10 mt-24 grid grid-cols-12 gap-6 items-start">
        <div className="col-span-5">
          <img src="/images/editorial/still-water.jpg" alt="Still water among coastal rocks." data-testid="breathwork-water-image" className="w-full aspect-[4/5] object-cover" />
        </div>
        <div className="col-span-7 self-center">
          <p className="pull-quote text-[22px] sm:text-[28px] px-2 sm:px-10">The breath is the one rhythm you carry everywhere. We simply slow it down.</p>
        </div>
      </section>

      <section className="max-w-[820px] mx-auto px-6 sm:px-10 mt-28 text-center">
        <Reveal>
          <div className="label-eyebrow">A quiet note</div>
          <p className="mt-8 text-[16px] leading-[1.9] text-[#55524C]">
            These sessions are about nervous system regulation and slowing down — practical, gentle, and grounded. They are not therapy and make no medical claims. If something deserves professional care, I will always say so, warmly.
          </p>
          <p className="mt-6 text-[16px] leading-[1.9] text-[#2E2B27]">
            Breathwork opens in September and is not yet bookable. Leave your details below and I'll write to you personally when sessions open.
          </p>
          <div className="text-left inline-block w-full max-w-xl">
            <BreathworkWaitlistForm />
          </div>
        </Reveal>
      </section>
    </div>
  );
}
